import { useEffect, useMemo, useState } from 'react'
import {
  GetLiveMonitorReadingsSubscription,
  GetLiveMonitorReadingsSubscriptionVariables,
} from '../../../generated/graphql'
import GetLiveMonitorReadings from '../graphql/subscriptions/GetLiveMonitorReadings'
import useManualSubscription from './useManualSubscription'

const WINDOW_MS = 30 * 60 * 1000
const REFRESH_INTERVAL_MS = 60 * 1000

export type UseLiveCallMonitorReadingsConfig = { skip?: boolean }

// Hasura subscriptions keep the variables they were opened with, so the lookahead/staleness
// cutoffs are refreshed on an interval and the subscription is re-opened to slide the window forward.
export default function useLiveCallMonitorReadings({
  skip = false,
}: UseLiveCallMonitorReadingsConfig = {}) {
  const [cutoffs, setCutoffs] = useState(() => ({
    upcomingCutoff: new Date(Date.now() + WINDOW_MS).toISOString(),
    staleCutoff: new Date(Date.now() - WINDOW_MS).toISOString(),
  }))

  useEffect(() => {
    if (skip) return

    const interval = setInterval(() => {
      setCutoffs({
        upcomingCutoff: new Date(Date.now() + WINDOW_MS).toISOString(),
        staleCutoff: new Date(Date.now() - WINDOW_MS).toISOString(),
      })
    }, REFRESH_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [skip])

  const { data, loading, error } = useManualSubscription<
    GetLiveMonitorReadingsSubscription,
    GetLiveMonitorReadingsSubscriptionVariables
  >({
    query: GetLiveMonitorReadings,
    variables: cutoffs,
    skip,
    restartKey: `${cutoffs.upcomingCutoff}|${cutoffs.staleCutoff}`,
  })

  const readings = useMemo(() => data?.reading ?? [], [data?.reading])

  return { readings, loading, error }
}
